/**
 * ProductAutocomplete — autocomplete pour lignes facture/devis.
 * Utilise PopoverAnchor pour NE PAS voler le focus à l'Input.
 * Tape 2 lettres → popover suggestions catalogue triées par usage.
 * Clic ou Entrée sur une suggestion → auto-remplit description + prix + TVA.
 */
import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverAnchor,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Package } from "lucide-react";

const fmtEUR = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} €`;

const CATEGORY_STYLE = {
  "SaaS":         "bg-blue-50 text-blue-700 border-blue-200",
  "Prestation":   "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Matériel":     "bg-amber-50 text-amber-700 border-amber-200",
  "Location":     "bg-violet-50 text-violet-700 border-violet-200",
  "Maintenance":  "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Autre":        "bg-slate-50 text-slate-600 border-slate-200",
};

export default function ProductAutocomplete({
  id, name,
  value, onChange, onSelectProduct, placeholder = "Désignation…",
  "data-testid": dataTestId, className,
}) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const v = value || "";
    if (!v || v.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/products/search`, { params: { q: v, limit: 8 } });
        const list = r.data?.products || [];
        setSuggestions(list);
        setOpen(list.length > 0);
        setHighlightIdx(-1);
      } catch { /* silent */ }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  const handleSelect = (p) => {
    setOpen(false);
    onSelectProduct?.(p);
    // Refocus input for continued edition
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((h) => Math.max(h - 1, -1));
    } else if (e.key === "Enter" && highlightIdx >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <Popover open={open && suggestions.length > 0} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Input
          id={id}
          name={name}
          ref={inputRef}
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          placeholder={placeholder}
          className={className}
          data-testid={dataTestId}
          autoComplete="off"
        />
      </PopoverAnchor>
      <PopoverContent
        className="p-0 w-[min(480px,90vw)]"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Ne pas fermer si le clic est sur l'input lui-même
          if (inputRef.current?.contains(e.target)) e.preventDefault();
        }}>
        <div className="p-2 border-b bg-slate-50 text-[10px] text-slate-500 uppercase font-medium tracking-wider flex justify-between">
          <span>Catalogue · {suggestions.length}</span>
          <span className="text-slate-400 normal-case">↑↓ Enter</span>
        </div>
        <div className="max-h-[280px] overflow-y-auto">
          {suggestions.map((p, idx) => (
            <button key={p.id} type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(p); }}
              onMouseEnter={() => setHighlightIdx(idx)}
              className={`w-full flex items-start gap-2 px-3 py-2 text-left border-b last:border-b-0
                ${highlightIdx === idx ? "bg-blue-50" : "hover:bg-slate-50"}`}
              data-testid={`product-suggestion-${p.id}`}>
              <Package className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-900 truncate">{p.name}</p>
                  <span className="text-sm font-mono font-semibold text-slate-800 whitespace-nowrap">
                    {fmtEUR(p.unit_price_ht)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className={`text-[10px] py-0 px-1.5 ${CATEGORY_STYLE[p.category] || CATEGORY_STYLE.Autre}`}>
                    {p.category}
                  </Badge>
                  <span className="text-[11px] text-slate-500">
                    TVA {p.vat_rate}% · /{p.unit || "u"}
                  </span>
                  {p.usage_count > 0 && (
                    <span className="text-[11px] text-slate-400">
                      · Utilisé {p.usage_count}×
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
